import { ReactNode } from 'react'
import clsx from 'clsx'

//  Page header
export function PageHeader({ title, subtitle, children }: {
  title: string; subtitle?: string; children?: ReactNode
}) {
  return (
    <div className="ra-flex ra-items-start ra-justify-between ra-mb-6">
      <div>
        <h1 className="ra-text-xl ra-font-bold ra-text-slate-800">{title}</h1>
        {subtitle && <p className="ra-text-sm ra-text-slate-500 ra-mt-0.5">{subtitle}</p>}
      </div>
      {children && <div className="ra-flex ra-items-center ra-gap-2">{children}</div>}
    </div>
  )
}

//  Stat card
export function StatCard({ label, value, unit, sub, color = 'blue' }: {
  label: string; value: string | number; unit?: string
  sub?: string; color?: 'blue' | 'emerald' | 'amber' | 'red' | 'violet'
}) {
  const dot = {
    blue:    'ra-bg-blue-500',
    emerald: 'ra-bg-emerald-500',
    amber:   'ra-bg-amber-500',
    red:     'ra-bg-red-500',
    violet:  'ra-bg-violet-500',
  }[color]

  return (
    <div className="ra-card">
      <div className="ra-flex ra-items-center ra-gap-2 ra-mb-2">
        <span className={clsx('ra-w-2 ra-h-2 ra-rounded-full', dot)} />
        <span className="ra-card-header">{label}</span>
      </div>
      <div className="ra-flex ra-items-baseline ra-gap-1">
        <span className="ra-stat-value">{value}</span>
        {unit && <span className="ra-text-slate-400 ra-text-sm">{unit}</span>}
      </div>
      {sub && <p className="ra-text-xs ra-text-slate-400 ra-mt-1">{sub}</p>}
    </div>
  )
}

//  Spinner
export function Spinner() {
  return (
    <div className="ra-flex ra-items-center ra-justify-center ra-h-48">
      <div className="ra-spinner" />
    </div>
  )
}

//  Error state
export function ErrorState({ message }: { message: string }) {
  return (
    <div className="ra-flex ra-flex-col ra-items-center ra-justify-center ra-h-48 ra-text-center">
      <p className="ra-text-red-500 ra-font-medium">Failed to load data</p>
      <p className="ra-text-slate-400 ra-text-sm ra-mt-1">{message}</p>
    </div>
  )
}

//  Empty state
export function EmptyState({ message = 'No data available' }: { message?: string }) {
  return (
    <div className="ra-flex ra-items-center ra-justify-center ra-h-48">
      <p className="ra-text-slate-400 ra-text-sm">{message}</p>
    </div>
  )
}

//  Chart card
export function ChartCard({ title, subtitle, children, className }: {
  title: string; subtitle?: string; children: ReactNode; className?: string
}) {
  return (
    <div className={clsx('ra-card', className)}>
      <div className="ra-mb-4">
        <h2 className="ra-text-sm ra-font-semibold ra-text-slate-700">{title}</h2>
        {subtitle && <p className="ra-text-xs ra-text-slate-400 ra-mt-0.5">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}

//  Meter selector
export function MeterSelect({ meters, value, onChange }: {
  meters: string[]; value: string; onChange: (v: string) => void
}) {
  return (
    <select className="ra-select-sm" value={value} onChange={e => onChange(e.target.value)}>
      {meters.map(m => <option key={m} value={m}>{m}</option>)}
    </select>
  )
}

//  Days selector
export function DaysSelect({ value, onChange, options = [7, 14, 30, 60, 90] }: {
  value: number; onChange: (v: number) => void; options?: number[]
}) {
  return (
    <select className="ra-select-sm" value={value} onChange={e => onChange(Number(e.target.value))}>
      {options.map(d => <option key={d} value={d}>Last {d} days</option>)}
    </select>
  )
}

//  Chart tooltip
export function ChartTooltip({ active, payload, label, unit = '' }: any) {
  if (!active || !payload?.length) return null
  return (
    <div className="ra-tooltip">
      <p className="ra-text-slate-400 ra-mb-1">{label}</p>
      {payload.map((p: any, i: number) => (
        <p key={i} style={{ color: p.color }} className="ra-font-mono ra-font-semibold">
          {p.name}: {p.value != null ? Number(p.value).toFixed(3) : '—'} {unit}
        </p>
      ))}
    </div>
  )
}
